// 楽天銀行の入出金明細CSVをZeniの取引インポート形式に変換する。
//
// 楽天銀行のWeb（入出金明細 > ダウンロード）から取得できるCSVは Shift-JIS で、次の列を持つ:
//   取引日, 入出金(円), 取引後残高(円), 入出金内容
// 他行のCSVと違い入金列・出金列が分かれておらず、1つの「入出金(円)」列の符号で向きを表す
// （マイナスが出金）。日付は区切り無しの "YYYYMMDD"。
// 残高列は口座残高の再計算には使わない（取り込んだ取引の差分で口座残高が更新されるため、
// 古い期間のCSVを取り込んだときに残高が巻き戻るのを避ける）。

import {
  MERCHANT_CATEGORY,
  matchKeywordCategory,
  normalizeDate,
  parseAmount,
  resolveFallbackCategories,
  splitCsvLine,
  splitCsvLines,
  type CsvCategoryRef,
  type CsvImportRow,
  type CsvParseResult,
} from './csv-common'

export type RakutenBankCategoryRef = CsvCategoryRef
export type RakutenBankImportRow = CsvImportRow
export type RakutenBankParseResult = CsvParseResult

// 店名キーワードより先に判定する銀行固有の摘要。
// 楽天銀行の入出金内容は全角・半角カナで振込先名や公金の名称がそのまま入るため、
// 店名表（「セブン」＝食費 など）に引っ張られる前にここで確定させる。
const BANK_EXPENSE_OVERRIDE: ReadonlyArray<readonly [string, string]> = [
  ['ＡＴＭ', 'other'], ['ATM', 'other'], ['カード引出', 'other'], ['引出', 'other'],
  ['振込手数料', 'other'], ['手数料', 'other'],
  // 税金・公金。楽天銀行の摘要はカナ書き（「タチカワシ　シミンゼイ．トミンゼイ」）になる
  ['住民税', 'other'], ['所得税', 'other'], ['国税', 'other'], ['地方税', 'other'], ['税金', 'other'],
  ['シミンゼイ', 'other'], ['トミンゼイ', 'other'], ['ケンミンゼイ', 'other'], ['ジユウミンゼイ', 'other'],
  ['ゼイムシヨ', 'other'], ['税務署', 'other'], ['年金', 'other'], ['ネンキン', 'other'],
  ['社会保険料', 'other'], ['国庫金', 'other'],
  // 他行への振込は摘要が相手銀行名になる。店名表に載る名前を含む銀行
  // （ドコモＳＭＴＢ→通信費、イオン銀行→食費 など）へ誤分類されるのを防ぐ。
  ['銀行', 'other'], ['ドコモSMTB', 'other'], ['SMTBネツト', 'other'], ['スミシンエスビ', 'other'],
  // グループ内の資金移動。「楽天モバイル」＝通信費を潰さないよう、カードと証券だけを指定する
  ['楽天カード', 'other'], ['ラクテンカ－ド', 'other'], ['ラクテンカード', 'other'],
  ['楽天証券', 'other'], ['ラクテンシヨウケン', 'other'],
]

// 入金行の摘要から収入カテゴリを推定する。一致しなければ「その他収入」へ。
const BANK_INCOME_CATEGORY: ReadonlyArray<readonly [string, string]> = [
  ['給与', 'salary'], ['給料', 'salary'], ['キユウヨ', 'salary'], ['キュウヨ', 'salary'],
  ['賞与', 'salary'], ['ボーナス', 'salary'],
  ['利息', 'income_other'], ['配当', 'income_other'], ['還付', 'income_other'], ['返金', 'income_other'],
  ['給付', 'income_other'], ['キユウフキン', 'income_other'], ['キュウフキン', 'income_other'],
]

interface ColumnMap {
  date: number
  amount: number
  desc: number
}

// ヘッダー行から各列の位置を特定する。見つからない列は既定の並び順にフォールバックする。
// 「入出金(円)」と「入出金内容」はどちらも「入出金」を含むので、金額列は内容・残高を除外して探す。
function locateColumns(header: string[]): ColumnMap {
  const find = (match: (h: string) => boolean, fallback: number) => {
    const idx = header.findIndex(match)
    return idx >= 0 ? idx : fallback
  }
  return {
    date: find((h) => h.includes('取引日') || h.includes('日付'), 0),
    amount: find((h) => h.includes('入出金') && !h.includes('内容') && !h.includes('残高'), 1),
    desc: find((h) => h.includes('内容') || h.includes('摘要'), 3),
  }
}

// 口座名やダウンロード日時が先頭に付くCSVもあり得るため、ヘッダー行を先頭数行から探す。
function findHeaderIndex(lines: string[]): number {
  const limit = Math.min(lines.length, 10)
  for (let i = 0; i < limit; i++) {
    const cells = splitCsvLine(lines[i])
    const hasDate = cells.some((c) => c.includes('取引日') || c.includes('日付'))
    const hasAmount = cells.some((c) => c.includes('入出金') || c.includes('金額'))
    if (hasDate && hasAmount) return i
  }
  return 0
}

// 入出金内容は最終列で、引用符なしのままカンマを含むことがある。
// 最終列以降を連結し直して、摘要が途中で切れるのを防ぐ。
function joinTrailing(cols: string[], from: number, isLast: boolean): string {
  return (isLast ? cols.slice(from).join(',') : (cols[from] ?? '')).trim()
}

export function parseRakutenBankCsv(
  text: string,
  accountId: string,
  categories: ReadonlyArray<RakutenBankCategoryRef>,
): RakutenBankParseResult {
  const lines = splitCsvLines(text)
  if (lines.length === 0) return { rows: [], parsed: 0, ignored: 0 }

  const headerIndex = findHeaderIndex(lines)
  const header = splitCsvLine(lines[headerIndex])
  const col = locateColumns(header)
  const descIsLast = col.desc >= header.length - 1

  const { expenseIds, incomeIds, fallbackExpense, fallbackIncome } = resolveFallbackCategories(categories)
  const expenseSet = new Set(expenseIds)
  const incomeSet = new Set(incomeIds)

  const rows: RakutenBankImportRow[] = []
  let ignored = 0

  for (const line of lines.slice(headerIndex + 1)) {
    const cols = splitCsvLine(line)
    const date = normalizeDate(cols[col.date] ?? '')
    const signed = parseAmount(cols[col.amount] ?? '')

    // 符号付きの1列だけなので、マイナスを出金・プラスを入金として扱う。0円行は無視する。
    if (!date || !Number.isFinite(signed) || signed === 0) {
      ignored++
      continue
    }
    const isIncome = signed > 0
    const amount = Math.abs(signed)

    const description = joinTrailing(cols, col.desc, descIsLast) || '楽天銀行'
    const category = isIncome
      ? matchKeywordCategory(description, BANK_INCOME_CATEGORY, incomeSet) ?? fallbackIncome
      : matchKeywordCategory(description, BANK_EXPENSE_OVERRIDE, expenseSet)
        ?? matchKeywordCategory(description, MERCHANT_CATEGORY, expenseSet)
        ?? fallbackExpense

    rows.push({
      date,
      type: isIncome ? 'income' : 'expense',
      category,
      description,
      amount,
      accountId,
      currency: 'JPY',
    })
  }

  return { rows, parsed: rows.length, ignored }
}
